import React, { useState, ReactNode, ReactElement } from "react";
import RecurrentPaymentContext from "./context";
import { Params, RentingDetails } from "./types";
import RecurrentPayment from "../../../../../app/domain/RecurrentPayment";
import fetchRecurrentPayments from "../../../../../usecases/recurrent-payments/fetch-recurrent-payments";
import fetchRecurrentPaymentDetail from "../../../../../usecases/recurrent-payments/fetch-recurrent-payment-detail";
import { MonetaryAmount } from "../../../../../app/domain/monetary-amount";

interface Props {
  children: ReactNode;
}

export default function RecurrentPaymentsWrapper(props: Props): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recurrentPayments, setRecurrentPayments] = useState<
    RecurrentPayment[]
  >([]);
  const [recurrentPaymentDetail, setRecurrentPaymentDetail] = useState(null);

  const [totalItems, setTotalItems] = useState<number>(-1);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const [rentingInitialData, setRentingInitialData] = useState(null);
  const [rentingDetails, setRentingDetails] = useState<RentingDetails>({
    VATSupportedBy: "",
    amountPerPayment: MonetaryAmount.ZERO,
    energyEfficiency: 0,
    firstPaymentAmount: MonetaryAmount.ZERO,
    firstPaymentOriginalAmount: MonetaryAmount.ZERO,
    firstPaymentTimestamp: 0,
    initialPaymentByCompany: MonetaryAmount.ZERO,
    initialPaymentByEmployee: MonetaryAmount.ZERO,
    lastPaymentTimestamp: 0,
    monthlyAttribution: MonetaryAmount.ZERO,
    numberOfPayments: 0,
    originalAmountPerPayment: MonetaryAmount.ZERO,
    privateUseRatio: 0,
    rentingCostPerPaymentCompany: MonetaryAmount.ZERO,
    rentingCostPerPaymentEmployee: MonetaryAmount.ZERO,
    serviceStartTimestamp: 0,
    totalAmount: MonetaryAmount.ZERO,
    vehiclePrice: MonetaryAmount.ZERO,
    yearlyAttribution: MonetaryAmount.ZERO,
  });

  const loadRecurrentPayments = async ({
    companyId,
    params,
    type,
  }: {
    companyId: string;
    params: Params;
    type: string;
  }) => {
    if (initialLoad) {
      setInitialLoad(false);
    }
    if (type !== "loadMore") {
      setIsLoading(true);
    }
    await fetchRecurrentPayments({
      companyId,
      params,
      callback: (data, extraData) => {
        if (type === "load" || type === "loadMore") {
          setRecurrentPayments((prev) => [...prev, ...data]);
        } else {
          setRecurrentPayments(data);
        }
        setTotalItems(extraData);
      },
    });
    if (type !== "loadMore") {
      setIsLoading(false);
    }
  };

  const loadRecurrentPaymentDetail = async ({
    companyId,
    recurrentPaymentId,
  }: {
    companyId: string;
    recurrentPaymentId: string;
  }) => {
    setIsLoading(true);
    await fetchRecurrentPaymentDetail({
      companyId,
      recurrentPaymentId,
      callback: (data) => setRecurrentPaymentDetail(data),
    });
    setIsLoading(false);
  };

  return (
    <RecurrentPaymentContext.Provider
      value={{
        recurrentPayments,
        setRecurrentPayments,
        isLoading,
        setIsLoading,
        initialLoad,
        setInitialLoad,
        totalItems,
        setTotalItems,
        recurrentPaymentDetail,
        setRecurrentPaymentDetail,
        rentingInitialData,
        setRentingInitialData,
        rentingDetails,
        setRentingDetails,
        loadRecurrentPayments,
        loadRecurrentPaymentDetail,
      }}
      {...props}
    />
  );
}
